import { Service } from '../../model/service/Service';
import i18n from "../../i18n";
import { ServiceInput } from './ServiceInput';

const API_URL = process.env.REACT_APP_PRODUCT_SERVICE_BASE_URL || '';

const SERVICE_FIELDS = `
    id
    businessId
    name
    description
    categories
    color
    showOnWebSite
    image
    totalDuration
    startPreparationDuration
    endPreparationDuration
    allowBookingDuringPreparationDuration
    startAfterDuration
    endAfterDuration
    allowBookingDuringAfterDuration
    price
    priceType
    kind
    capacity
    taxRate
    depositRequired
    amountDepositRequired
    allowRecurring
    isHealthDataOverride
    isHealthDataResolved
    recurrenceFreqs
    maxRecurrenceMonths
    allowOnlineBooking
    bookingWindow
    allowExternalLocation
    acceptedPlans {
        healthPlanId
        negotiatedValue
        repasseType
        repasseValue
    }
`;

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors.map((e: { message: string }) => e.message).join(', ');
        throw new Error(msg);
    }
    return json.data as T;
}

export async function getAllServices(token: string): Promise<Service[]> {
    const data = await gqlPost<{ getAllServices: Service[] | null }>(
        token,
        `query { getAllServices { ${SERVICE_FIELDS} } }`
    );
    return data.getAllServices ?? [];
}

/**
 * Indexed lookup — only the services for one business come back from Mongo.
 * Prefer this over getAllServices + a client-side filter; on Atlas, the all-
 * collection scan was the bottleneck on the Services screen.
 */
export async function getServicesByBusinessId(
    businessId: number | string,
    token: string
): Promise<Service[]> {
    const data = await gqlPost<{ getServicesByBusinessId: Service[] | null }>(
        token,
        `query getServicesByBusinessId($businessId: ID!) {
            getServicesByBusinessId(businessId: $businessId) { ${SERVICE_FIELDS} }
        }`,
        { businessId }
    );
    return data.getServicesByBusinessId ?? [];
}

export async function getServiceById(id: string, token: string): Promise<Service | null> {
    const data = await gqlPost<{ getServiceById: Service | null }>(
        token,
        `query getServiceById($id: ID!) { getServiceById(id: $id) { ${SERVICE_FIELDS} } }`,
        { id }
    );
    return data.getServiceById ?? null;
}

export async function saveService(input: ServiceInput, token: string): Promise<Service> {
    const data = await gqlPost<{ saveService: Service }>(
        token,
        `mutation saveService($input: ServiceInput!) {
            saveService(serviceInput: $input) { ${SERVICE_FIELDS} }
        }`,
        { input }
    );
    return data.saveService;
}

export async function deleteService(id: string, token: string): Promise<boolean> {
    const data = await gqlPost<{ deleteService: boolean }>(
        token,
        `mutation deleteService($id: ID!) { deleteService(id: $id) }`,
        { id }
    );
    return !!data.deleteService;
}
